import analyticSender from '../helpers/analyticSender';
import uuid from 'uuid';
import omit from 'lodash/omit';
const emptyObject = () => ({});
import { mapGetters, mapState } from 'vuex';

export default {
  props: {
    actionData: {
      type: Object,
      default: emptyObject,
    },
    api: {
      type: Object,
      default: emptyObject,
    },
    participants: {
      type: Array,
      default: () => [],
    },
    selectedParticipants: {
      type: Array,
      default: () => [],
    },
    group: {
      type: Object,
      default: emptyObject,
    },
  },

  data() {
    return {
      title: 'Action',
      contexts: [],
      actionCachedData: {},
    };
  },

  computed: {
    ...mapState('rooms', ['roomInfo']),
    ...mapGetters('rooms', ['isHost']),
    infoForAction() {
      const info = {
        ...this.roomInfo,
        roomName: this.roomInfo?.room_name,
      };
      const omitInfoKeys = ['token', 'room_token', 'room', 'id'];
      return omit(info, omitInfoKeys);
    },
    isDefaultGroup() {
      return this.group?.id === 'all';
    },
    activeParticipants() {
      if (this.selectedParticipants?.length) {
        return this.selectedParticipants;
      }
      return this.includedParticipants(this.group);
    },
    selectedCount() {
      return this.selectedParticipants.length;
    },
    maxOrder() {
      return this.participants.reduce((prev, curr) => {
        return prev > curr.order ? prev : curr.order;
      }, 0);
    },
    groupParticipants() {
      if (!this.group) return this.participants;
      return this.participants.filter((i) => i.groups.includes(this.group.id));
    },
    hostParticipants() {
      return this.participants.reduce((acc, i) => {
        if (i.type === 'host') {
          acc.push({
            identity: i.identity,
            name: i.name,
            room: i.room,
            type: i.type,
          });
        }
        return acc;
      }, []);
    },
    guestParticipants() {
      return this.participants.filter((i) => i.type === 'guest');
    },
    participantsConnected() {
      return this.participants.length;
    },
    showActionControls() {
      return this.isHost && this.contexts.length;
    },
    actionContentClass() {
      return this.isMobile ? 'pa-2' : 'pa-4';
    },
    currentContextState() {
      const currentContext = this.contexts.find(
        (c) => c.groupId === this.group.id
      );
      return currentContext?.state || {};
    },
  },

  methods: {
    track(event, data) {
      analyticSender(event, data);
    },

    isHostIdentity(identity) {
      return identity.indexOf('host_') === 0;
    },

    getSingleParticipant(identity) {
      return this.participants.find((p) => p.identity === identity);
    },

    showActionContent(state) {
      return !this.isHost && state.show;
    },

    previousParticipants(group) {
      return this.participants.filter((part) => {
        return part.previousGroup === group.id;
      });
    },

    includedParticipants(group) {
      return this.participants.filter((part) => {
        if (group.id === 'all') {
          return part.groups.length === 1;
        }
        return part.groups.indexOf(group.id) !== -1;
      });
    },

    notImplemented(method) {
      console.warn(this.title + `.${method}() not implemented`);
      return false;
    },

    addContext(state = {}) {
      let contexts = this.contexts.slice(0);
      const newContext = {
        groupId: this.group.id,
        id: uuid.v1(),
        state,
      };

      contexts.push(newContext);
      this.setContexts(contexts);
    },

    clearContextForGroup(clearDefaultGroup = false) {
      if (!this.group || !this.contexts.length) return;

      this.activeParticipants.forEach((part) => {
        setTimeout(() => {
          this.sendActionTo(part, { show: false });
        });
      });

      // if some participants selected we clear previous actions for them.
      // if not - for whole group
      if (!this.selectedCount) {
        let contexts = this.contexts.filter((i) => i.groupId !== this.group.id);
        this.setContexts(contexts);
      }
    },

    removeContext(context) {
      this.setContexts(this.contexts.filter((c) => c.id !== context.id));
    },

    setContexts(contexts) {
      this.contexts = contexts;
      this.api.trigger('stateChange', { contexts });
    },

    selected() {
      return this.notImplemented('selected');
    },

    received(actionMessage) {
      return this.notImplemented('received');
    },

    groupRemoved(group) {
      let groupContext = this.contexts.find((c) => c.groupId === group.id);
      if (groupContext) {
        this.closeAction(groupContext);
      }
    },

    closeAction(context) {
      context.state.show = false;
      this.updateParticipants(context);
      this.removeContext(context);
    },

    updateParticipants(c) {
      const participants = this.includedParticipants({
        id: c.groupId,
      });

      participants.forEach((part) => {
        setTimeout(() => {
          this.sendActionTo(part, c.state);
        });
      });

      if (!participants.length) {
        this.sendActionTo({}, c.state);
      }
    },

    sendActionTo(participant, data = {}, context = {}) {
      data.to = { identity: participant?.identity };
      return this.api.send(data, context);
    },
  },

  mounted() {
    this.api.on('groupRemoved', (group) => {
      this.groupRemoved(group);
    });

    this.api.on('stateUpdate', (state) => {
      for (let s in state) {
        this[s] = state[s];
      }
    });

    this.api.on('sendState', () => {
      this.api.trigger('stateChange', {
        contexts: this.contexts,
      });
    });
  },
};
